import React, { useEffect, useState } from "react";
import { FiDownload } from "react-icons/fi";
import { useComponentTranslation } from "@/services/i18n";
import { downloadResume } from "@/utils";
import api from "@/services/api";
import Loader from "@/components/loader";

const translations = {
  en: {
    resume: "Resume",
    download_resume: "Download Resume",
    no_resume: "No resume available",
    resume_preview_unavailable: "Resume preview is not available for this file type",
    error_loading_resume: "Failed to load resume preview",
  },
  fr: {
    resume: "CV",
    download_resume: "Télécharger le CV",
    no_resume: "Aucun CV disponible",
    resume_preview_unavailable: "L'aperçu du CV n'est pas disponible pour ce type de fichier",
    error_loading_resume: "Impossible de charger l'aperçu du CV",
  },
};

export default function ResumeTab({ candidate }) {
  const { t } = useComponentTranslation(translations);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(!!candidate.resume_url);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objectUrl;

    if (!candidate.resume_url || !candidate._id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(false);
    setPreviewUrl(null);

    (async () => {
      try {
        const { blob } = await api.downloadFile(`/user/${candidate._id}/resume`);
        objectUrl = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setPreviewUrl(objectUrl);
      } catch (e) {
        console.error("Error loading resume preview:", e);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [candidate._id, candidate.resume_url]);

  if (!candidate.resume_url) {
    return (
      <div className="p-5 bg-gray-50 rounded-lg border border-gray-200 text-center">
        <p className="text-sm text-gray-500">{t("no_resume")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button type="button" onClick={() => downloadResume(candidate)} className="btn btn-primary inline-flex items-center gap-2">
          <FiDownload className="w-4 h-4" />
          {t("download_resume")}
        </button>
      </div>
      <div className="border border-gray-200 rounded-lg p-5 bg-gray-50">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader />
          </div>
        ) : previewUrl ? (
          <iframe src={previewUrl} className="w-full h-[600px] border border-gray-200 rounded" title={t("resume")} />
        ) : (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded">
            <p className="text-sm text-blue-800">{error ? t("error_loading_resume") : t("resume_preview_unavailable")}</p>
          </div>
        )}
      </div>
    </div>
  );
}
